import type {
  AgentWorkspaceRequirementActor,
  AgentWorkspaceTargetFormat,
} from "@/features/agent-workspace";
import type {
  CogoConnectedDocumentProviderResource,
  CogoStudioConnectedDocumentPublicationReceipt,
  CogoStudioConnectedDocumentSnapshot,
} from "../connected-document-contract";
import {
  createCogoDocumentGraph,
  isCogoDocumentGraph,
  materializeCogoDocumentAnchor,
  updateCogoDocumentNode,
  type CogoDocumentGraph,
} from "./document-graph";
import type { CogoDocumentTemplateGenerationState } from "./document-template-generation";
import {
  isCogoDocumentTemplateFamilyAuthoringState,
  type CogoDocumentTemplateFamilyAuthoringState,
} from "../template-v2/template-family-authoring-model";

export const COGO_DOCUMENT_WORKSPACE_SCHEMA_VERSION =
  "cogo.document-workspace.v1" as const;

export const COGO_DOCUMENT_OPERATION_SCHEMA_VERSION =
  "cogo.document-operation.v1" as const;

export const COGO_DOCUMENT_PROPOSAL_SCHEMA_VERSION =
  "cogo.document-proposal.v1" as const;

export const COGO_DOCUMENT_EXPORT_REQUEST_SCHEMA_VERSION =
  "cogo.document-export-request.v1" as const;

export const COGO_DOCUMENT_CONNECTED_SOURCE_SCHEMA_VERSION =
  "cogo.document-connected-source.v1" as const;

export const COGO_DOCUMENT_CONNECTED_PUBLICATION_SCHEMA_VERSION =
  "cogo.document-connected-publication.v1" as const;

export const COGO_DOCUMENT_EXPORT_TARGETS = [
  "docx",
  "hwpx",
  "pptx",
  "pdf",
  "xlsx",
] as const;

export type CogoDocumentExportTarget =
  (typeof COGO_DOCUMENT_EXPORT_TARGETS)[number];

/**
 * Schema vocabulary (`COGO_DOCUMENT_EXPORT_TARGETS`) is kernel knowledge —
 * every format the workspace model can name. The Studio commercial export
 * surface is narrower: charter 5D / §7.1, the operator may request docx
 * only. Renderer `/ready` may still advertise pdf; that is not a Studio
 * product offer.
 */
export const STUDIO_COMMERCIAL_DOCUMENT_EXPORT_TARGETS = ["docx"] as const;

export type StudioCommercialDocumentExportTarget =
  (typeof STUDIO_COMMERCIAL_DOCUMENT_EXPORT_TARGETS)[number];

export function isStudioCommercialDocumentExportTarget(
  target: string,
): target is StudioCommercialDocumentExportTarget {
  return (
    STUDIO_COMMERCIAL_DOCUMENT_EXPORT_TARGETS as readonly string[]
  ).includes(target);
}

export type CogoDocumentNodeKind =
  | "document"
  | "page"
  | "section"
  | "paragraph"
  | "heading"
  | "table"
  | "row"
  | "cell"
  | "chart"
  | "image"
  | "slide"
  | "slide-object"
  | "formula-binding"
  | "export-profile";

export type CogoDocumentOperationKind =
  | "updateParagraphText"
  | "replaceTextRange"
  | "updateHeading"
  | "updateTableCellText"
  | "updateTableLayoutIntent"
  | "updateChartBinding"
  | "updateSlideObjectText"
  | "updateSlideObjectFrame"
  | "updateExportProfile"
  | "inspectReadbackDiff"
  | "promoteFixToRule";

export type CogoDocumentProposalStatus =
  | "proposed"
  | "approved"
  | "rejected"
  | "blocked";

export type CogoDocumentOperationStatus =
  | "proposed"
  | "approved"
  | "applied"
  | "blocked";

export type CogoDocumentWorkingSurface =
  | "wordgard-draft"
  | "cogo-preview-page-editor"
  | "cogo-preview-readback"
  | "cogo-workbook-inspector";

export interface CogoDocumentOperation {
  schemaVersion: typeof COGO_DOCUMENT_OPERATION_SCHEMA_VERSION;
  operationId: string;
  kind: CogoDocumentOperationKind;
  status: CogoDocumentOperationStatus;
  documentId: string;
  documentNodeId: string;
  documentNodeKind: CogoDocumentNodeKind;
  previewAnchorId?: string;
  sourceLocatorIds: string[];
  affectedExportTargets: CogoDocumentExportTarget[];
  workingSurface: CogoDocumentWorkingSurface;
  baseRevision: number;
  nextRevision?: number;
  actor: AgentWorkspaceRequirementActor;
  reason: string;
  payload: Record<string, unknown>;
  proofRefs: string[];
  createdAt: string;
  approvedAt?: string;
  appliedAt?: string;
  mayPatchCogoJsonDirectly: false;
}

export interface CogoDocumentProposal {
  schemaVersion: typeof COGO_DOCUMENT_PROPOSAL_SCHEMA_VERSION;
  proposalId: string;
  status: CogoDocumentProposalStatus;
  documentId: string;
  documentNodeId: string;
  documentNodeKind: CogoDocumentNodeKind;
  previewAnchorId?: string;
  body: string;
  operation: CogoDocumentOperation;
  baseRevision: number;
  createdBy: AgentWorkspaceRequirementActor;
  createdAt: string;
  decidedAt?: string;
  blockers: string[];
  mayPatchCogoJsonDirectly: false;
}

export interface CogoDocumentExportRequest {
  schemaVersion: typeof COGO_DOCUMENT_EXPORT_REQUEST_SCHEMA_VERSION;
  requestId: string;
  documentId: string;
  target: CogoDocumentExportTarget;
  scope: "document" | "page";
  pageId?: string;
  status: "requested" | "ready" | "blocked";
  baseRevision: number;
  createdAt: string;
  proofRefs: string[];
  blockers: string[];
}

export interface CogoDocumentConnectedSourceBinding {
  schemaVersion: typeof COGO_DOCUMENT_CONNECTED_SOURCE_SCHEMA_VERSION;
  bindingId: string;
  pageId: string;
  sourceArtifactRef: string;
  capturedAt: string;
  providerResource: CogoConnectedDocumentProviderResource;
  snapshotStatus: CogoStudioConnectedDocumentSnapshot["status"];
  versionAuthority: CogoStudioConnectedDocumentSnapshot["versionAuthority"];
  blockNodeIds: Record<string, string>;
  unsupportedElements: CogoStudioConnectedDocumentSnapshot["unsupportedElements"];
  blockers: string[];
  readOnlySnapshot: true;
}

export interface CogoDocumentConnectedPublicationRecord {
  schemaVersion: typeof COGO_DOCUMENT_CONNECTED_PUBLICATION_SCHEMA_VERSION;
  bindingId: string;
  workspaceRevision: number;
  sourceVersionRef: string;
  receipt: CogoStudioConnectedDocumentPublicationReceipt;
}

export interface CogoDocumentWorkspace {
  schemaVersion: typeof COGO_DOCUMENT_WORKSPACE_SCHEMA_VERSION;
  documentId: string;
  title: string;
  revision: number;
  graph: CogoDocumentGraph;
  proposals: CogoDocumentProposal[];
  operations: CogoDocumentOperation[];
  exportRequests: CogoDocumentExportRequest[];
  connectedSources: CogoDocumentConnectedSourceBinding[];
  connectedPublicationReceipts: CogoDocumentConnectedPublicationRecord[];
  templateGeneration: CogoDocumentTemplateGenerationState | null;
  templateAuthoring: CogoDocumentTemplateFamilyAuthoringState | null;
  adapterPolicy: {
    authority: "cogo-document-model";
    wordgardAuthority: "draft-collaboration-only";
    previewAuthority: "page-slide-pdf-layout";
    mayPatchCogoJsonDirectly: false;
  };
}

export interface CogoDocumentProposalConflict {
  conflictId: string;
  documentId: string;
  documentNodeId: string;
  baseRevision: number;
  proposalIds: string[];
  actors: AgentWorkspaceRequirementActor[];
  status: "open";
  reason: "same-node-same-base";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function metadataOf(project: unknown): Record<string, unknown> {
  if (!isRecord(project)) return {};
  const settings = isRecord(project.settings) ? project.settings : {};
  const settingsMetadata = isRecord(settings.cogoApplicationMetadata)
    ? settings.cogoApplicationMetadata
    : {};
  const projectMetadata = isRecord(project.metadata) ? project.metadata : {};
  return {
    ...settingsMetadata,
    ...projectMetadata,
  };
}

function uniqueStrings(values: Array<string | undefined | null>): string[] {
  return values.filter((value, index, items): value is string => {
    return (
      typeof value === "string" &&
      value.length > 0 &&
      items.indexOf(value) === index
    );
  });
}

function uniqueTargets(
  values: Array<string | undefined | null>,
): CogoDocumentExportTarget[] {
  const targets = values.filter((value): value is CogoDocumentExportTarget =>
    COGO_DOCUMENT_EXPORT_TARGETS.includes(value as CogoDocumentExportTarget),
  );
  return Array.from(new Set(targets));
}

function sanitizeId(value: string): string {
  return value.replaceAll(/[^a-zA-Z0-9._-]/g, "-");
}

function isHttpsUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

function isSha256Hash(value: unknown): value is string {
  return typeof value === "string" && /^sha256:[a-f0-9]{64}$/.test(value);
}

function isCogoDocumentOperation(
  value: unknown,
): value is CogoDocumentOperation {
  return (
    isRecord(value) &&
    value.schemaVersion === COGO_DOCUMENT_OPERATION_SCHEMA_VERSION &&
    typeof value.operationId === "string" &&
    value.mayPatchCogoJsonDirectly === false
  );
}

function isCogoDocumentProposal(value: unknown): value is CogoDocumentProposal {
  return (
    isRecord(value) &&
    value.schemaVersion === COGO_DOCUMENT_PROPOSAL_SCHEMA_VERSION &&
    typeof value.proposalId === "string" &&
    isCogoDocumentOperation(value.operation) &&
    value.mayPatchCogoJsonDirectly === false
  );
}

function isCogoDocumentExportRequest(
  value: unknown,
): value is CogoDocumentExportRequest {
  return (
    isRecord(value) &&
    value.schemaVersion === COGO_DOCUMENT_EXPORT_REQUEST_SCHEMA_VERSION &&
    typeof value.requestId === "string"
  );
}

function isCogoDocumentConnectedSourceBinding(
  value: unknown,
): value is CogoDocumentConnectedSourceBinding {
  if (
    !isRecord(value) ||
    value.schemaVersion !== COGO_DOCUMENT_CONNECTED_SOURCE_SCHEMA_VERSION ||
    typeof value.bindingId !== "string" ||
    typeof value.pageId !== "string" ||
    typeof value.sourceArtifactRef !== "string" ||
    typeof value.capturedAt !== "string" ||
    !["ready", "blocked"].includes(String(value.snapshotStatus)) ||
    value.readOnlySnapshot !== true ||
    !isRecord(value.blockNodeIds) ||
    !Object.values(value.blockNodeIds).every(
      (nodeId) => typeof nodeId === "string",
    ) ||
    !Array.isArray(value.unsupportedElements) ||
    !Array.isArray(value.blockers) ||
    !value.blockers.every((blocker) => typeof blocker === "string")
  ) {
    return false;
  }
  const providerResource = value.providerResource;
  return (
    isRecord(providerResource) &&
    providerResource.schemaVersion === "cogo.connected-document-resource.v1" &&
    ["google-docs", "notion"].includes(String(providerResource.provider)) &&
    typeof providerResource.connectionRef === "string" &&
    typeof providerResource.resourceId === "string" &&
    isHttpsUrl(providerResource.canonicalUrl) &&
    typeof providerResource.versionRef === "string" &&
    providerResource.writePolicy === "new-copy"
  );
}

export function isCogoConnectedDocumentPublicationReceipt(
  value: unknown,
): value is CogoStudioConnectedDocumentPublicationReceipt {
  if (
    !isRecord(value) ||
    value.schemaVersion !==
      "cogo.studio-connected-document-publication-receipt.v1" ||
    !["pass", "fail", "blocked"].includes(String(value.status)) ||
    !["google-docs", "notion"].includes(String(value.provider)) ||
    typeof value.sourceResourceId !== "string" ||
    value.sourceResourceId.length === 0 ||
    typeof value.sourceVersionRef !== "string" ||
    value.sourceVersionRef.length === 0 ||
    typeof value.expectedStructureHash !== "string" ||
    (value.expectedStyleProfileHash !== undefined &&
      typeof value.expectedStyleProfileHash !== "string") ||
    (value.observedStyleProfileHash !== undefined &&
      typeof value.observedStyleProfileHash !== "string") ||
    (value.targetCanonicalUrl !== undefined &&
      !isHttpsUrl(value.targetCanonicalUrl)) ||
    !Array.isArray(value.exportReadbacks) ||
    !value.exportReadbacks.every(
      (readback) =>
        isRecord(readback) &&
        typeof readback.mimeType === "string" &&
        typeof readback.contentType === "string" &&
        typeof readback.byteLength === "number" &&
        readback.byteLength > 0 &&
        typeof readback.sha256 === "string" &&
        readback.sha256.length > 0,
    ) ||
    !Array.isArray(value.blockers) ||
    !value.blockers.every((blocker) => typeof blocker === "string")
  ) {
    return false;
  }
  if (value.status !== "pass") return true;
  const contentHashesMatch =
    typeof value.expectedContentHash === "string" &&
    value.expectedContentHash.length > 0 &&
    value.observedContentHash === value.expectedContentHash;
  const structureHashesMatch =
    value.expectedStructureHash.length > 0 &&
    typeof value.observedStructureHash === "string" &&
    value.observedStructureHash === value.expectedStructureHash;
  const hasTargetProof =
    typeof value.targetResourceId === "string" &&
    value.targetResourceId.length > 0 &&
    typeof value.targetVersionRef === "string" &&
    value.targetVersionRef.length > 0 &&
    isHttpsUrl(value.targetCanonicalUrl);
  const exportReadbacks = value.exportReadbacks as Array<{
    mimeType: string;
  }>;
  const hasGoogleExports =
    value.provider !== "google-docs" ||
    [
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/pdf",
    ].every((mimeType) =>
      exportReadbacks.some((readback) => readback.mimeType === mimeType),
    );
  const hasGoogleStyleProfileProof =
    value.provider !== "google-docs" ||
    (isSha256Hash(value.expectedStyleProfileHash) &&
      isSha256Hash(value.observedStyleProfileHash) &&
      value.expectedStyleProfileHash === value.observedStyleProfileHash);
  return (
    hasTargetProof &&
    contentHashesMatch &&
    structureHashesMatch &&
    value.blockers.length === 0 &&
    hasGoogleExports &&
    hasGoogleStyleProfileProof
  );
}

function isCogoDocumentConnectedPublicationRecord(
  value: unknown,
): value is CogoDocumentConnectedPublicationRecord {
  return (
    isRecord(value) &&
    value.schemaVersion ===
      COGO_DOCUMENT_CONNECTED_PUBLICATION_SCHEMA_VERSION &&
    typeof value.bindingId === "string" &&
    Number.isInteger(value.workspaceRevision) &&
    Number(value.workspaceRevision) >= 0 &&
    typeof value.sourceVersionRef === "string" &&
    value.sourceVersionRef.length > 0 &&
    isCogoConnectedDocumentPublicationReceipt(value.receipt) &&
    value.receipt.sourceVersionRef === value.sourceVersionRef
  );
}

function normalizeConnectedDocumentPublicationRecords(input: {
  values: unknown[];
  connectedSources: CogoDocumentConnectedSourceBinding[];
  workspaceRevision: number;
}): CogoDocumentConnectedPublicationRecord[] {
  return input.values.map((value, index) => {
    if (isCogoDocumentConnectedPublicationRecord(value)) return value;
    const receipt = value as CogoStudioConnectedDocumentPublicationReceipt;
    const inferredBinding = [...input.connectedSources]
      .reverse()
      .find(
        (binding) =>
          binding.providerResource.provider === receipt.provider &&
          binding.providerResource.resourceId === receipt.sourceResourceId,
      );
    return {
      schemaVersion: COGO_DOCUMENT_CONNECTED_PUBLICATION_SCHEMA_VERSION,
      bindingId:
        inferredBinding?.bindingId ??
        `legacy.${receipt.provider}.${sanitizeId(receipt.sourceResourceId)}.${index + 1}`,
      workspaceRevision: input.workspaceRevision,
      sourceVersionRef: receipt.sourceVersionRef,
      receipt,
    };
  });
}

export function isCogoDocumentWorkspace(
  value: unknown,
): value is CogoDocumentWorkspace {
  return (
    isRecord(value) &&
    value.schemaVersion === COGO_DOCUMENT_WORKSPACE_SCHEMA_VERSION &&
    typeof value.documentId === "string" &&
    typeof value.title === "string" &&
    typeof value.revision === "number" &&
    isCogoDocumentGraph(value.graph) &&
    Array.isArray(value.proposals) &&
    value.proposals.every(isCogoDocumentProposal) &&
    Array.isArray(value.operations) &&
    value.operations.every(isCogoDocumentOperation) &&
    Array.isArray(value.exportRequests) &&
    value.exportRequests.every(isCogoDocumentExportRequest) &&
    Array.isArray(value.connectedSources) &&
    value.connectedSources.every(isCogoDocumentConnectedSourceBinding) &&
    Array.isArray(value.connectedPublicationReceipts) &&
    value.connectedPublicationReceipts.every(
      isCogoDocumentConnectedPublicationRecord,
    ) &&
    (value.templateGeneration === null ||
      (isRecord(value.templateGeneration) &&
        value.templateGeneration.schemaVersion ===
          "cogo.document-template-generation-state.v1" &&
        value.templateGeneration.automatedWinner === null &&
        value.templateGeneration.modelCanonicalAuthority === false &&
        value.templateGeneration.modelFinalWriterAllowed === false)) &&
    (value.templateAuthoring === null ||
      isCogoDocumentTemplateFamilyAuthoringState(value.templateAuthoring)) &&
    isRecord(value.adapterPolicy) &&
    value.adapterPolicy.mayPatchCogoJsonDirectly === false
  );
}

export function createCogoDocumentWorkspace(input: {
  documentId: string;
  title: string;
  pageTitle?: string;
  revision?: number;
  graph?: CogoDocumentGraph;
  proposals?: CogoDocumentProposal[];
  operations?: CogoDocumentOperation[];
  exportRequests?: CogoDocumentExportRequest[];
  connectedSources?: CogoDocumentConnectedSourceBinding[];
  connectedPublicationReceipts?: CogoDocumentConnectedPublicationRecord[];
  templateGeneration?: CogoDocumentTemplateGenerationState | null;
  templateAuthoring?: CogoDocumentTemplateFamilyAuthoringState | null;
}): CogoDocumentWorkspace {
  return {
    schemaVersion: COGO_DOCUMENT_WORKSPACE_SCHEMA_VERSION,
    documentId: input.documentId,
    title: input.title,
    revision: input.revision ?? 1,
    graph:
      input.graph ??
      createCogoDocumentGraph({
        documentId: input.documentId,
        title: input.title,
        pageTitle: input.pageTitle,
      }),
    proposals: input.proposals ?? [],
    operations: input.operations ?? [],
    exportRequests: input.exportRequests ?? [],
    connectedSources: input.connectedSources ?? [],
    connectedPublicationReceipts: input.connectedPublicationReceipts ?? [],
    templateGeneration: input.templateGeneration ?? null,
    templateAuthoring: input.templateAuthoring ?? null,
    adapterPolicy: {
      authority: "cogo-document-model",
      wordgardAuthority: "draft-collaboration-only",
      previewAuthority: "page-slide-pdf-layout",
      mayPatchCogoJsonDirectly: false,
    },
  };
}

export function readCogoDocumentWorkspaceFromProject(
  project: unknown,
): CogoDocumentWorkspace | null {
  const workspace = metadataOf(project).documentWorkspace;
  if (isCogoDocumentWorkspace(workspace)) return workspace;
  if (
    !isRecord(workspace) ||
    workspace.schemaVersion !== COGO_DOCUMENT_WORKSPACE_SCHEMA_VERSION ||
    typeof workspace.documentId !== "string" ||
    typeof workspace.title !== "string" ||
    typeof workspace.revision !== "number" ||
    !Array.isArray(workspace.proposals) ||
    !workspace.proposals.every(isCogoDocumentProposal) ||
    !Array.isArray(workspace.operations) ||
    !workspace.operations.every(isCogoDocumentOperation) ||
    !Array.isArray(workspace.exportRequests) ||
    !workspace.exportRequests.every(isCogoDocumentExportRequest) ||
    (workspace.connectedSources !== undefined &&
      (!Array.isArray(workspace.connectedSources) ||
        !workspace.connectedSources.every(
          isCogoDocumentConnectedSourceBinding,
        ))) ||
    (workspace.connectedPublicationReceipts !== undefined &&
      (!Array.isArray(workspace.connectedPublicationReceipts) ||
        !workspace.connectedPublicationReceipts.every(
          (value) =>
            isCogoDocumentConnectedPublicationRecord(value) ||
            isCogoConnectedDocumentPublicationReceipt(value),
        ))) ||
    (workspace.templateGeneration !== undefined &&
      workspace.templateGeneration !== null &&
      (!isRecord(workspace.templateGeneration) ||
        workspace.templateGeneration.schemaVersion !==
          "cogo.document-template-generation-state.v1" ||
        workspace.templateGeneration.automatedWinner !== null ||
        workspace.templateGeneration.modelCanonicalAuthority !== false ||
        workspace.templateGeneration.modelFinalWriterAllowed !== false)) ||
    (workspace.templateAuthoring !== undefined &&
      workspace.templateAuthoring !== null &&
      !isCogoDocumentTemplateFamilyAuthoringState(workspace.templateAuthoring))
  ) {
    return null;
  }
  let graph = isCogoDocumentGraph(workspace.graph)
    ? workspace.graph
    : createCogoDocumentGraph({
        documentId: workspace.documentId,
        title: workspace.title,
      });
  if (!isCogoDocumentGraph(workspace.graph)) {
    for (const proposal of workspace.proposals) {
      graph = materializeCogoDocumentAnchor({
        graph,
        nodeId: proposal.documentNodeId,
        nodeKind: proposal.documentNodeKind,
        sourceLocatorIds: proposal.operation.sourceLocatorIds,
        status: proposal.status === "approved" ? "active" : "proposed",
        now: proposal.createdAt,
      });
    }
    for (const operation of workspace.operations) {
      graph = applyCogoDocumentOperationToGraph(graph, operation);
    }
  }
  const connectedSources =
    (workspace.connectedSources as
      | CogoDocumentConnectedSourceBinding[]
      | undefined) ?? [];
  const connectedPublicationReceipts =
    workspace.connectedPublicationReceipts === undefined
      ? []
      : normalizeConnectedDocumentPublicationRecords({
          values: workspace.connectedPublicationReceipts,
          connectedSources,
          workspaceRevision: workspace.revision,
        });
  return {
    ...(workspace as unknown as Omit<
      CogoDocumentWorkspace,
      | "graph"
      | "connectedSources"
      | "connectedPublicationReceipts"
      | "templateGeneration"
      | "templateAuthoring"
    >),
    graph,
    exportRequests: workspace.exportRequests.map((request) => ({
      ...request,
      scope: request.scope === "page" ? "page" : "document",
    })),
    connectedSources,
    connectedPublicationReceipts,
    templateGeneration:
      (workspace.templateGeneration as
        | CogoDocumentTemplateGenerationState
        | null
        | undefined) ?? null,
    templateAuthoring:
      (workspace.templateAuthoring as
        | CogoDocumentTemplateFamilyAuthoringState
        | null
        | undefined) ?? null,
  };
}

export function ensureCogoDocumentWorkspace(input: {
  project: unknown;
  documentId?: string;
  title?: string;
  pageTitle?: string;
}): CogoDocumentWorkspace {
  return (
    readCogoDocumentWorkspaceFromProject(input.project) ??
    createCogoDocumentWorkspace({
      documentId: input.documentId ?? "cogo-document-main",
      title: input.title ?? "Coogo document",
      pageTitle: input.pageTitle,
    })
  );
}

export function upsertCogoDocumentWorkspaceToProject(
  project: unknown,
  workspace: CogoDocumentWorkspace,
): Record<string, unknown> {
  const projectRecord = isRecord(project) ? project : {};
  const metadata = metadataOf(projectRecord);
  return {
    ...projectRecord,
    metadata: {
      ...metadata,
      documentWorkspace: workspace,
    },
  };
}

export function restoreCogoDocumentWorkspaceToProject(
  project: unknown,
  workspace: CogoDocumentWorkspace | null,
): Record<string, unknown> {
  if (workspace)
    return upsertCogoDocumentWorkspaceToProject(project, workspace);
  const projectRecord = isRecord(project) ? structuredClone(project) : {};
  if (isRecord(projectRecord.metadata)) {
    delete projectRecord.metadata.documentWorkspace;
  }
  if (isRecord(projectRecord.settings)) {
    const applicationMetadata = isRecord(
      projectRecord.settings.cogoApplicationMetadata,
    )
      ? projectRecord.settings.cogoApplicationMetadata
      : null;
    if (applicationMetadata) delete applicationMetadata.documentWorkspace;
  }
  return projectRecord;
}

export function resolveCogoDocumentWorkingSurface(input: {
  operationKind: CogoDocumentOperationKind;
  documentNodeKind: CogoDocumentNodeKind;
  affectedExportTargets: CogoDocumentExportTarget[];
}): CogoDocumentWorkingSurface {
  const primaryTarget = input.affectedExportTargets[0] ?? "docx";
  if (primaryTarget === "pdf") return "cogo-preview-readback";
  if (primaryTarget === "pptx") return "cogo-preview-page-editor";
  if (primaryTarget === "xlsx") return "cogo-workbook-inspector";

  const narrativeOperation =
    input.operationKind === "updateParagraphText" ||
    input.operationKind === "replaceTextRange" ||
    input.operationKind === "updateHeading";
  const narrativeNode =
    input.documentNodeKind === "document" ||
    input.documentNodeKind === "section" ||
    input.documentNodeKind === "paragraph" ||
    input.documentNodeKind === "heading";

  if (
    (primaryTarget === "docx" || primaryTarget === "hwpx") &&
    narrativeOperation &&
    narrativeNode
  ) {
    return "wordgard-draft";
  }
  return "cogo-preview-page-editor";
}

export function createCogoDocumentEditProposal(input: {
  proposalId?: string;
  operationId?: string;
  documentId: string;
  documentNodeId: string;
  documentNodeKind: CogoDocumentNodeKind;
  body: string;
  operationKind: CogoDocumentOperationKind;
  baseRevision: number;
  actor?: AgentWorkspaceRequirementActor;
  reason?: string;
  previewAnchorId?: string;
  sourceLocatorIds?: string[];
  affectedExportTargets?: Array<
    CogoDocumentExportTarget | AgentWorkspaceTargetFormat
  >;
  payload?: Record<string, unknown>;
  proofRefs?: string[];
  requestedSurface?: CogoDocumentWorkingSurface;
  createdAt?: string;
}): CogoDocumentProposal {
  const createdAt = input.createdAt ?? new Date().toISOString();
  const affectedExportTargets = uniqueTargets(
    input.affectedExportTargets ?? ["docx"],
  );
  const workingSurface = resolveCogoDocumentWorkingSurface({
    operationKind: input.operationKind,
    documentNodeKind: input.documentNodeKind,
    affectedExportTargets,
  });
  const blockers =
    input.requestedSurface === "wordgard-draft" &&
    workingSurface !== "wordgard-draft"
      ? [
          "WordGard is limited to DOCX/HWPX narrative collaboration; this target must use the Coogo Preview/page editor or an export check.",
        ]
      : [];
  const status: CogoDocumentProposalStatus =
    blockers.length > 0 ? "blocked" : "proposed";
  const proposalId =
    input.proposalId ??
    `doc-proposal-${input.baseRevision}-${sanitizeId(input.documentNodeId)}-${Date.now().toString(36)}`;
  const operationId = input.operationId ?? `${proposalId}.operation`;
  const operation: CogoDocumentOperation = {
    schemaVersion: COGO_DOCUMENT_OPERATION_SCHEMA_VERSION,
    operationId,
    kind: input.operationKind,
    status: status === "blocked" ? "blocked" : "proposed",
    documentId: input.documentId,
    documentNodeId: input.documentNodeId,
    documentNodeKind: input.documentNodeKind,
    previewAnchorId: input.previewAnchorId,
    sourceLocatorIds: uniqueStrings(input.sourceLocatorIds ?? []),
    affectedExportTargets,
    workingSurface,
    baseRevision: input.baseRevision,
    actor: input.actor ?? "user",
    reason: input.reason ?? input.body,
    payload: input.payload ?? { body: input.body },
    proofRefs: uniqueStrings(input.proofRefs ?? []),
    createdAt,
    mayPatchCogoJsonDirectly: false,
  };
  return {
    schemaVersion: COGO_DOCUMENT_PROPOSAL_SCHEMA_VERSION,
    proposalId,
    status,
    documentId: input.documentId,
    documentNodeId: input.documentNodeId,
    documentNodeKind: input.documentNodeKind,
    previewAnchorId: input.previewAnchorId,
    body: input.body,
    operation,
    baseRevision: input.baseRevision,
    createdBy: input.actor ?? "user",
    createdAt,
    blockers,
    mayPatchCogoJsonDirectly: false,
  };
}

function replaceProposal(
  proposals: CogoDocumentProposal[],
  next: CogoDocumentProposal,
): CogoDocumentProposal[] {
  const index = proposals.findIndex(
    (proposal) => proposal.proposalId === next.proposalId,
  );
  if (index === -1) return [...proposals, next];
  return proposals.map((proposal, proposalIndex) =>
    proposalIndex === index ? next : proposal,
  );
}

export function appendCogoDocumentProposal(
  workspace: CogoDocumentWorkspace,
  proposal: CogoDocumentProposal,
): CogoDocumentWorkspace {
  if (proposal.documentId !== workspace.documentId) {
    throw new Error(
      "Document proposal targets a different Coogo document workspace.",
    );
  }
  return {
    ...workspace,
    graph: materializeCogoDocumentAnchor({
      graph: workspace.graph,
      nodeId: proposal.documentNodeId,
      nodeKind: proposal.documentNodeKind,
      sourceLocatorIds: proposal.operation.sourceLocatorIds,
      status: "proposed",
      now: proposal.createdAt,
    }),
    proposals: replaceProposal(workspace.proposals, proposal),
  };
}

function payloadText(payload: Record<string, unknown>): string | undefined {
  for (const key of ["text", "replacementText", "body"]) {
    if (typeof payload[key] === "string") return payload[key] as string;
  }
  return undefined;
}

function applyCogoDocumentOperationToGraph(
  source: CogoDocumentGraph,
  operation: CogoDocumentOperation,
): CogoDocumentGraph {
  let graph = materializeCogoDocumentAnchor({
    graph: source,
    nodeId: operation.documentNodeId,
    nodeKind: operation.documentNodeKind,
    sourceLocatorIds: operation.sourceLocatorIds,
    status: "active",
    now: operation.appliedAt ?? operation.createdAt,
  });
  const text =
    operation.kind === "inspectReadbackDiff" ||
    operation.kind === "promoteFixToRule" ||
    operation.kind === "updateExportProfile"
      ? undefined
      : payloadText(operation.payload);
  const wordGardState = isRecord(operation.payload.wordGardState)
    ? structuredClone(operation.payload.wordGardState)
    : undefined;
  graph = updateCogoDocumentNode({
    graph,
    nodeId: operation.documentNodeId,
    text,
    status: "active",
    sourceLocatorIds: operation.sourceLocatorIds,
    attributes: {
      ...(wordGardState
        ? {
            wordGardState,
            wordGardSchemaVersion:
              typeof operation.payload.wordGardSchemaVersion === "string"
                ? operation.payload.wordGardSchemaVersion
                : "wordgard.state.v1",
          }
        : {}),
      lastOperationId: operation.operationId,
      lastOperationKind: operation.kind,
      affectedExportTargets: operation.affectedExportTargets,
    },
    now: operation.appliedAt ?? operation.createdAt,
  });
  return graph;
}

export function appendCogoDocumentProposalToProject(
  project: unknown,
  proposal: CogoDocumentProposal,
): Record<string, unknown> {
  const workspace = ensureCogoDocumentWorkspace({
    project,
    documentId: proposal.documentId,
  });
  return upsertCogoDocumentWorkspaceToProject(
    project,
    appendCogoDocumentProposal(workspace, proposal),
  );
}

function activeProposal(proposal: CogoDocumentProposal): boolean {
  return proposal.status === "proposed";
}

export function detectCogoDocumentProposalConflicts(
  proposals: CogoDocumentProposal[],
): CogoDocumentProposalConflict[] {
  const groups = new Map<string, CogoDocumentProposal[]>();
  for (const proposal of proposals.filter(activeProposal)) {
    const key = `${proposal.documentId}::${proposal.documentNodeId}::${proposal.baseRevision}`;
    groups.set(key, [...(groups.get(key) ?? []), proposal]);
  }
  return Array.from(groups.values())
    .filter((group) => group.length > 1)
    .map((group) => {
      const [first] = group;
      return {
        conflictId: `doc-conflict-${sanitizeId(first.documentNodeId)}-${first.baseRevision}`,
        documentId: first.documentId,
        documentNodeId: first.documentNodeId,
        baseRevision: first.baseRevision,
        proposalIds: group.map((proposal) => proposal.proposalId).sort(),
        actors: Array.from(
          new Set(group.map((proposal) => proposal.createdBy)),
        ).sort(),
        status: "open",
        reason: "same-node-same-base",
      };
    });
}

function proposalHasOpenConflict(
  proposal: CogoDocumentProposal,
  conflicts: CogoDocumentProposalConflict[],
): boolean {
  return conflicts.some((conflict) =>
    conflict.proposalIds.includes(proposal.proposalId),
  );
}

export function reviewCogoDocumentProposal(input: {
  proposal: CogoDocumentProposal;
  currentRevision: number;
  decision: "approve" | "reject";
  openConflicts?: CogoDocumentProposalConflict[];
  decidedAt?: string;
}): {
  proposal: CogoDocumentProposal;
  operation: CogoDocumentOperation | null;
  canApply: boolean;
  blockers: string[];
} {
  const decidedAt = input.decidedAt ?? new Date().toISOString();
  if (input.decision === "reject") {
    return {
      proposal: {
        ...input.proposal,
        status: "rejected",
        decidedAt,
        operation: {
          ...input.proposal.operation,
          status: "blocked",
        },
      },
      operation: null,
      canApply: false,
      blockers: [],
    };
  }

  const blockers = [
    ...input.proposal.blockers,
    ...(input.currentRevision !== input.proposal.baseRevision
      ? [
          "Document changed after this proposal was created; rebase before applying.",
        ]
      : []),
    ...(proposalHasOpenConflict(input.proposal, input.openConflicts ?? [])
      ? [
          "Multiple active proposals target the same document node and revision; choose one first.",
        ]
      : []),
  ];

  if (blockers.length > 0) {
    return {
      proposal: {
        ...input.proposal,
        status: "blocked",
        decidedAt,
        blockers,
        operation: {
          ...input.proposal.operation,
          status: "blocked",
        },
      },
      operation: null,
      canApply: false,
      blockers,
    };
  }

  const operation: CogoDocumentOperation = {
    ...input.proposal.operation,
    status: "approved",
    approvedAt: decidedAt,
    mayPatchCogoJsonDirectly: false,
  };
  return {
    proposal: {
      ...input.proposal,
      status: "approved",
      decidedAt,
      operation,
    },
    operation,
    canApply: true,
    blockers: [],
  };
}

export function applyCogoDocumentProposal(input: {
  workspace: CogoDocumentWorkspace;
  proposalId: string;
  decidedAt?: string;
  decision?: "approve" | "reject";
}): {
  workspace: CogoDocumentWorkspace;
  proposal: CogoDocumentProposal;
  operation: CogoDocumentOperation | null;
  blockers: string[];
} {
  const proposal = input.workspace.proposals.find(
    (candidate) => candidate.proposalId === input.proposalId,
  );
  if (!proposal)
    throw new Error(`Document proposal "${input.proposalId}" was not found.`);

  const conflicts = detectCogoDocumentProposalConflicts(
    input.workspace.proposals,
  );
  const review = reviewCogoDocumentProposal({
    proposal,
    currentRevision: input.workspace.revision,
    decision: input.decision ?? "approve",
    openConflicts: conflicts,
    decidedAt: input.decidedAt,
  });
  if (!review.canApply || !review.operation) {
    return {
      workspace: {
        ...input.workspace,
        proposals: replaceProposal(input.workspace.proposals, review.proposal),
      },
      proposal: review.proposal,
      operation: null,
      blockers: review.blockers,
    };
  }

  const nextRevision = input.workspace.revision + 1;
  const appliedAt = input.decidedAt ?? new Date().toISOString();
  const operation: CogoDocumentOperation = {
    ...review.operation,
    status: "applied",
    nextRevision,
    appliedAt,
    mayPatchCogoJsonDirectly: false,
  };
  const approvedProposal: CogoDocumentProposal = {
    ...review.proposal,
    operation,
  };
  return {
    workspace: {
      ...input.workspace,
      revision: nextRevision,
      graph: applyCogoDocumentOperationToGraph(
        input.workspace.graph,
        operation,
      ),
      proposals: replaceProposal(input.workspace.proposals, approvedProposal),
      operations: [...input.workspace.operations, operation],
    },
    proposal: approvedProposal,
    operation,
    blockers: [],
  };
}

export function applyCogoDocumentProposalToProject(input: {
  project: unknown;
  proposalId: string;
  decidedAt?: string;
  decision?: "approve" | "reject";
}): {
  project: Record<string, unknown>;
  workspace: CogoDocumentWorkspace;
  proposal: CogoDocumentProposal;
  operation: CogoDocumentOperation | null;
  blockers: string[];
} {
  const workspace = readCogoDocumentWorkspaceFromProject(input.project);
  if (!workspace)
    throw new Error("The current project has no Coogo document workspace.");
  const result = applyCogoDocumentProposal({
    workspace,
    proposalId: input.proposalId,
    decidedAt: input.decidedAt,
    decision: input.decision,
  });
  return {
    project: upsertCogoDocumentWorkspaceToProject(
      input.project,
      result.workspace,
    ),
    ...result,
  };
}

export function createCogoDocumentExportRequest(input: {
  workspace: CogoDocumentWorkspace;
  target: CogoDocumentExportTarget;
  pageId?: string;
  requestId?: string;
  status?: "requested" | "ready" | "blocked";
  proofRefs?: string[];
  blockers?: string[];
  createdAt?: string;
}): CogoDocumentExportRequest {
  return {
    schemaVersion: COGO_DOCUMENT_EXPORT_REQUEST_SCHEMA_VERSION,
    requestId:
      input.requestId ??
      `doc-export-${input.workspace.revision}-${input.target}-${Date.now().toString(36)}`,
    documentId: input.workspace.documentId,
    target: input.target,
    scope: input.pageId ? "page" : "document",
    pageId: input.pageId,
    status: input.status ?? "requested",
    baseRevision: input.workspace.revision,
    createdAt: input.createdAt ?? new Date().toISOString(),
    proofRefs: uniqueStrings(input.proofRefs ?? []),
    blockers: input.blockers ?? [],
  };
}

export function appendCogoDocumentExportRequest(
  workspace: CogoDocumentWorkspace,
  request: CogoDocumentExportRequest,
): CogoDocumentWorkspace {
  return {
    ...workspace,
    exportRequests: [...workspace.exportRequests, request],
  };
}

export function appendCogoDocumentExportRequestToProject(input: {
  project: unknown;
  target: CogoDocumentExportTarget;
  pageId?: string;
  status?: "requested" | "ready" | "blocked";
  proofRefs?: string[];
  blockers?: string[];
  requestId?: string;
  createdAt?: string;
}): {
  project: Record<string, unknown>;
  workspace: CogoDocumentWorkspace;
  request: CogoDocumentExportRequest;
} {
  const workspace = ensureCogoDocumentWorkspace({ project: input.project });
  const request = createCogoDocumentExportRequest({
    workspace,
    target: input.target,
    pageId: input.pageId,
    requestId: input.requestId,
    status: input.status,
    proofRefs: input.proofRefs,
    blockers: input.blockers,
    createdAt: input.createdAt,
  });
  const nextWorkspace = appendCogoDocumentExportRequest(workspace, request);
  return {
    project: upsertCogoDocumentWorkspaceToProject(input.project, nextWorkspace),
    workspace: nextWorkspace,
    request,
  };
}

export function inspectCogoDocumentWorkspace(project: unknown): {
  workspace: CogoDocumentWorkspace | null;
  proposalCount: number;
  appliedOperationCount: number;
  pendingProposalCount: number;
  exportRequestCount: number;
  pageCount: number;
  nodeCount: number;
  blockers: string[];
} {
  const workspace = readCogoDocumentWorkspaceFromProject(project);
  if (!workspace) {
    return {
      workspace: null,
      proposalCount: 0,
      appliedOperationCount: 0,
      pendingProposalCount: 0,
      exportRequestCount: 0,
      pageCount: 0,
      nodeCount: 0,
      blockers: ["No Coogo document workspace exists for the current project."],
    };
  }
  return {
    workspace,
    proposalCount: workspace.proposals.length,
    appliedOperationCount: workspace.operations.filter(
      (operation) => operation.status === "applied",
    ).length,
    pendingProposalCount: workspace.proposals.filter(
      (proposal) => proposal.status === "proposed",
    ).length,
    exportRequestCount: workspace.exportRequests.length,
    pageCount: workspace.graph.pageOrder.length,
    nodeCount: Object.keys(workspace.graph.nodes).length,
    blockers: [],
  };
}
